import { ProcessingFeeBasis } from "@redotech/crdb-enums/fulfillment/processing-fee-basis";
import { Currency, CurrencyNames } from "@redotech/money/currencies";
import { readReturnsCenterId } from "@redotech/redo-return-core-common/returns-center-id";
import {
  EXTERNAL_PROCESSING_BILLING_ENABLED,
  PROCESSING_CURRENCIES,
} from "@redotech/redo-return-settings-common/processing-billing-config";
import { DEFAULT_RETURNS_PROCESSING_COST } from "@redotech/redo-return-settings-common/redo-managed-returns-settings";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoButtonTabGroup } from "@redotech/redo-web/arbiter-components/checkbox/redo-button-tab-group";
import { RedoCollapseSubsection } from "@redotech/redo-web/arbiter-components/collapse/redo-collapse-section";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import {
  externalProcessingFeeErrors,
  processingFeeErrors,
  type RedoManagedReturnsSettingsForm,
  type ReturnsCenterConfigFormValue,
} from "../returns-package-protection/v2/redo-managed-returns-settings-form";
import { AddReturnsCenterException } from "./returns-center-exception-picker";
import { useReturnsCenterRows } from "./use-returns-center-rows";

const basisOptions = [
  { value: ProcessingFeeBasis.PerReturn, el: "Per return" },
  { value: ProcessingFeeBasis.PerItem, el: "Per unit" },
];
const currencyOptions: RedoListItem<Currency | null>[] = [
  { type: "text", id: "inherit", text: "Use merchant currency", value: null },
  ...PROCESSING_CURRENCIES.map((value) => ({
    type: "text" as const,
    id: value,
    text: `${value} — ${CurrencyNames[value]}`,
    value,
  })),
];

interface ProcessingBillingExceptionsProps {
  input: RedoManagedReturnsSettingsForm;
  disabled: boolean;
}

export const ProcessingBillingExceptions = memo(
  function ProcessingBillingExceptions({
    input,
    disabled,
  }: ProcessingBillingExceptionsProps) {
    const centerInput = input.inputs.availableReturnsCenters;
    const values = centerInput.value ?? [];
    const centers = useReturnsCenterRows(values);
    const [addedIds, setAddedIds] = useState<readonly string[]>([]);
    const pricingFor = (id: string) =>
      values.find(
        (value) =>
          readReturnsCenterId(value.returnsCenterId)?.toString() === id,
      );
    const exceptions = centers.filter((center) => {
      const pricing = pricingFor(center.id);
      const hasOverride =
        pricing != null &&
        (pricing.processingFee.trim() !== "" ||
          pricing.processingFeeBasis != null ||
          pricing.externalProcessingFee.trim() !== "" ||
          pricing.externalProcessingFeeBasis != null ||
          pricing.processingCurrency != null);
      return addedIds.includes(center.id) || hasOverride;
    });
    const addable = centers.filter((center) => !exceptions.includes(center));
    const defaultFee =
      input.inputs.processingCost.value ||
      String(DEFAULT_RETURNS_PROCESSING_COST);
    const merchantExternalFee = input.inputs.externalProcessingFee.value;
    const defaultExternalFee = merchantExternalFee || defaultFee;
    const defaultBasis = input.inputs.processingFeeBasis.value;
    const defaultExternalBasis = input.inputs.externalProcessingFeeBasis.value;
    const defaultCurrency = input.inputs.processingCurrency.value;
    const update = (
      id: string,
      changes: Partial<ReturnsCenterConfigFormValue>,
    ) => {
      setAddedIds((ids) => (ids.includes(id) ? ids : [...ids, id]));
      centerInput.setValue(
        values.map((value) =>
          readReturnsCenterId(value.returnsCenterId)?.toString() === id
            ? { ...value, ...changes }
            : value,
        ),
      );
    };
    return (
      <Flex dir="column" pt="xl">
        <RedoCollapseSubsection
          defaultCollapsed={false}
          title={`Return center overrides (${exceptions.length})`}
        >
          <Flex dir="column" gap="md">
            {exceptions.length > 0 && (
              <Flex
                borderColor="primary"
                borderRadius="lg"
                borderWidth="1px"
                dir="column"
                overflow="hidden"
              >
                {exceptions.map((center, index) => {
                  const pricing = pricingFor(center.id);
                  if (!pricing) {
                    return null;
                  }
                  const basis = pricing.processingFeeBasis ?? defaultBasis;
                  const externalBasis =
                    pricing.externalProcessingFeeBasis ?? defaultExternalBasis;
                  const currency =
                    pricing.processingCurrency ?? defaultCurrency;
                  return (
                    <Flex
                      borderColor="secondary"
                      borderStyle="solid"
                      borderTopWidth={index > 0 ? "1px" : "none"}
                      dir="column"
                      gap="md"
                      key={center.id}
                      px="xl"
                      py="lg"
                    >
                      <Flex align="center" gap="md" justify="space-between">
                        <Flex dir="column" gap="xxs" minW="none">
                          <Text fontSize="xs" fontWeight="medium">
                            {center.name}
                          </Text>
                          {center.location && (
                            <Text fontSize="xxs" textColor="secondary">
                              {center.location}
                            </Text>
                          )}
                        </Flex>
                        <RedoButton
                          disabled={disabled}
                          hierarchy="link"
                          onClick={() => {
                            update(center.id, {
                              processingFee: "",
                              processingFeeBasis: null,
                              processingCurrency: null,
                              externalProcessingFee: "",
                              externalProcessingFeeBasis: null,
                            });
                            setAddedIds((ids) =>
                              ids.filter((id) => id !== center.id),
                            );
                          }}
                          size="xs"
                          text="Use merchant defaults"
                        />
                      </Flex>
                      <Flex gap="md">
                        <Flex basis={0} dir="column" gap="xs" grow={1}>
                          <Text
                            fontSize="xs"
                            fontWeight="medium"
                            textColor="secondary"
                          >
                            Fee basis
                          </Text>
                          <RedoButtonTabGroup
                            disabled={disabled}
                            fullWidth={false}
                            onChange={(value) =>
                              update(center.id, {
                                processingFee:
                                  pricing.processingFee || defaultFee,
                                processingFeeBasis: value,
                              })
                            }
                            options={basisOptions}
                            size="sm"
                            value={basis}
                          />
                        </Flex>
                        <Flex basis={0} dir="column" gap="xs" grow={1}>
                          <Text
                            fontSize="xs"
                            fontWeight="medium"
                            textColor="secondary"
                          >
                            Processing currency
                          </Text>
                          <RedoSingleSelectDropdownInput<Currency | null>
                            ariaLabel={`${center.name} processing currency`}
                            disabled={disabled}
                            options={currencyOptions}
                            optionSelected={({ item }) =>
                              update(center.id, {
                                processingCurrency: item.value,
                                processingFee:
                                  pricing.processingFee || defaultFee,
                                processingFeeBasis: basis,
                              })
                            }
                            selectedItem={currencyOptions.find(
                              (item) =>
                                item.value ===
                                (pricing.processingCurrency ?? null),
                            )}
                            size="xs"
                          />
                        </Flex>
                      </Flex>
                      <RedoTextInput
                        description={
                          pricing.processingFee === ""
                            ? `Uses merchant fee: ${defaultFee} ${currency}`
                            : "This center’s amount is pinned. Changing currency does not convert it."
                        }
                        errorText={
                          processingFeeErrors(pricing.processingFee)[0]
                        }
                        IconTrailing={() => <Text as="span">{currency}</Text>}
                        inputTypeHint="number"
                        label={
                          basis === ProcessingFeeBasis.PerItem
                            ? "Per-unit processing fee"
                            : "Per-return processing fee"
                        }
                        min={0}
                        placeholder={defaultFee}
                        setValue={(fee) =>
                          update(
                            center.id,
                            fee.trim() === ""
                              ? {
                                  processingFee: "",
                                  processingFeeBasis: null,
                                  processingCurrency: null,
                                }
                              : {
                                  processingFee: fee,
                                  processingFeeBasis: basis,
                                },
                          )
                        }
                        state={disabled ? "disabled" : "default"}
                        step={0.01}
                        value={pricing.processingFee}
                      />
                      {EXTERNAL_PROCESSING_BILLING_ENABLED && (
                        <RedoCollapseSubsection
                          defaultCollapsed
                          title="External returns"
                        >
                          <Flex dir="column" gap="md" pt="md">
                            <Text fontSize="xxs" textColor="secondary">
                              Optional rate for retail and bulk returns. If
                              unset, this center uses the merchant external rate
                              or its standard rate.
                            </Text>
                            <Flex align="flex-start" gap="md">
                              <Flex basis={0} dir="column" gap="xs" grow={1}>
                                <Text
                                  fontSize="xxs"
                                  fontWeight="medium"
                                  textColor="secondary"
                                >
                                  Fee basis
                                </Text>
                                <RedoButtonTabGroup
                                  disabled={disabled}
                                  fullWidth={false}
                                  onChange={(value) =>
                                    update(center.id, {
                                      externalProcessingFee:
                                        pricing.externalProcessingFee ||
                                        defaultExternalFee,
                                      externalProcessingFeeBasis: value,
                                    })
                                  }
                                  options={basisOptions}
                                  size="sm"
                                  value={externalBasis}
                                />
                              </Flex>
                              <Flex basis={0} dir="column" gap="xs" grow={1}>
                                <RedoTextInput
                                  description={
                                    pricing.externalProcessingFee === ""
                                      ? merchantExternalFee === ""
                                        ? "Uses this center’s standard rate"
                                        : `Uses merchant external rate: ${merchantExternalFee} ${currency}`
                                      : undefined
                                  }
                                  errorText={
                                    externalProcessingFeeErrors(
                                      pricing.externalProcessingFee,
                                    )[0]
                                  }
                                  IconTrailing={() => (
                                    <Text as="span">{currency}</Text>
                                  )}
                                  inputTypeHint="number"
                                  label={
                                    externalBasis === ProcessingFeeBasis.PerItem
                                      ? "Per-unit fee"
                                      : "Per-return fee"
                                  }
                                  min={0.01}
                                  placeholder={defaultExternalFee}
                                  setValue={(fee) =>
                                    update(center.id, {
                                      externalProcessingFee: fee,
                                      externalProcessingFeeBasis:
                                        fee.trim() === ""
                                          ? null
                                          : externalBasis,
                                    })
                                  }
                                  state={disabled ? "disabled" : "default"}
                                  step={0.01}
                                  value={pricing.externalProcessingFee}
                                />
                              </Flex>
                            </Flex>
                          </Flex>
                        </RedoCollapseSubsection>
                      )}
                    </Flex>
                  );
                })}
              </Flex>
            )}
            {addable.length > 0 && (
              <AddReturnsCenterException
                buttonText="Add override"
                centers={addable}
                disabled={disabled}
                onAdd={(id) => setAddedIds((ids) => [...ids, id])}
              />
            )}
          </Flex>
        </RedoCollapseSubsection>
      </Flex>
    );
  },
);
